import { twJoin } from "tailwind-merge";

import {
  ColumnsConfig,
  resolveColumnType,
} from "src/cases/caseView/blocks/cellTypes";
import { TypedCellDisplay } from "src/cases/caseView/blocks/TypedCellDisplay";

/** Shared cell chrome for the property-based diff and the shape-change
 *  snapshots, matching the row diff's header and cell styling. */

export const DiffTableHeaderCell: React.FC<{
  children?: React.ReactNode;
  dataLoc?: string;
  muted?: boolean;
  style?: React.CSSProperties;
}> = ({ children, dataLoc, muted, style }) => (
  <th
    className="border border-gray-100 bg-gray-50 p-0 text-left"
    data-loc={dataLoc}
    style={style}
  >
    <div
      className={twJoin(
        "flex h-8 items-center pr-2 pl-1.5 font-inter-medium-12px",
        muted ? "text-gray-500" : "text-gray-800",
      )}
    >
      <span className="truncate">{children}</span>
    </div>
  </th>
);

/** A cell value rendered with the column's configured type, wrapped so long
 *  strings grow the row instead of the column. */
export const DiffTableValueCell: React.FC<{
  value: unknown;
  hasValue: boolean;
  columnKey: string;
  columnsConfig: ColumnsConfig;
  dataLoc: string;
  muted?: boolean;
  tint: string | false;
  style?: React.CSSProperties;
}> = ({
  value,
  hasValue,
  columnKey,
  columnsConfig,
  dataLoc,
  muted,
  tint,
  style,
}) => {
  const displayType = resolveColumnType(
    columnsConfig?.[columnKey]?.display_config ?? null,
    value,
  );

  return (
    <td
      className={twJoin("border border-gray-100 p-0 align-top", tint)}
      data-loc={dataLoc}
      style={style}
    >
      <div
        className={twJoin(
          // contain-inline-size: as in the row diff, values must not size
          // the columns; long strings wrap inside the cell instead.
          "flex min-w-0 items-start px-1.5 py-1.5 font-inter-normal-12px contain-inline-size",
          muted ? "text-gray-500" : "text-gray-800",
        )}
      >
        {hasValue && (
          <TypedCellDisplay
            columnKey={columnKey}
            displayType={displayType}
            value={value}
            multiline
          />
        )}
      </div>
    </td>
  );
};
